import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Progress } from "../ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { toast } from "sonner";
import {
  Clock, Users, FolderOpen, RefreshCw, Calendar, User, Check, X,
  ChevronDown, ChevronUp, Timer, AlertCircle, Settings, TrendingUp,
  BarChart3, PieChart, Moon, Sun, AlertTriangle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function TimesheetsTab() {
  const [loading, setLoading] = useState(true);
  const [configStatus, setConfigStatus] = useState(null);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [summary, setSummary] = useState(null);
  const [weeklyData, setWeeklyData] = useState(null);
  const [dailyData, setDailyData] = useState(null);
  const [timeEntries, setTimeEntries] = useState([]);
  
  // After Hours state
  const [afterHoursData, setAfterHoursData] = useState(null);
  const [afterHoursLoading, setAfterHoursLoading] = useState(false);
  const [afterHoursWeeks, setAfterHoursWeeks] = useState(1);
  
  // Anomalies state
  const [anomaliesData, setAnomaliesData] = useState(null);
  const [anomaliesLoading, setAnomaliesLoading] = useState(false);
  
  // Filters
  const [selectedUser, setSelectedUser] = useState("all");
  const [weeksBack, setWeeksBack] = useState(4);
  const [daysBack, setDaysBack] = useState(7);
  
  // Sub-tab
  const [activeSubTab, setActiveSubTab] = useState("summary");
  
  // Config dialog
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [newApiKey, setNewApiKey] = useState("");
  const [configuring, setConfiguring] = useState(false);

  // Fetch configuration status
  const fetchConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/clockify/config`);
      setConfigStatus(res.data);
      return res.data.is_configured;
    } catch (error) {
      console.error("Failed to fetch Clockify config:", error);
      return false;
    }
  }, []);

  // Verify connection and load data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Verify connection first
      const verifyRes = await axios.get(`${API}/api/clockify/verify`);
      if (!verifyRes.data.success) {
        toast.error("Clockify connection failed: " + verifyRes.data.error);
        setLoading(false);
        return;
      }
      
      // Load users, projects, summary, and weekly in parallel (fast endpoints)
      const [usersRes, projectsRes, summaryRes, weeklyRes, dailyRes] = await Promise.all([
        axios.get(`${API}/api/clockify/users`),
        axios.get(`${API}/api/clockify/projects`),
        axios.get(`${API}/api/clockify/summary`),
        axios.get(`${API}/api/clockify/weekly?weeks=${weeksBack}`),
        axios.get(`${API}/api/clockify/daily?days=${daysBack}`)
      ]);
      
      setUsers(usersRes.data.users || []);
      setProjects(projectsRes.data.projects || []);
      setSummary(summaryRes.data);
      setWeeklyData(weeklyRes.data);
      setDailyData(dailyRes.data);
      
      // Note: Time entries will be loaded separately when the entries tab is clicked
      // as it can be slow with large datasets
      
      setLoading(false);
      toast.success("Timesheet data loaded");
    } catch (error) {
      console.error("Failed to load Clockify data:", error);
      toast.error("Failed to load timesheet data");
      setLoading(false);
    }
  }, [weeksBack, daysBack]);

  // Configure Clockify API
  const configureClockify = async () => {
    if (!newApiKey.trim()) {
      toast.error("Please enter an API key");
      return;
    }
    
    setConfiguring(true);
    try {
      const res = await axios.post(`${API}/api/clockify/configure?api_key=${encodeURIComponent(newApiKey.trim())}`);
      if (res.data.success) {
        toast.success("Clockify connected successfully!");
        setShowConfigDialog(false);
        setNewApiKey("");
        await fetchConfig();
        await loadData();
      } else {
        toast.error("Failed to connect: " + res.data.error);
      }
    } catch (error) {
      toast.error("Failed to configure Clockify");
    }
    setConfiguring(false);
  };

  // Initial load
  useEffect(() => {
    const init = async () => {
      const isConfigured = await fetchConfig();
      if (isConfigured) {
        await loadData();
      } else {
        setLoading(false);
      }
    };
    init();
  }, [fetchConfig, loadData]);

  // State for time entries loading
  const [entriesLoading, setEntriesLoading] = useState(false);

  // Load time entries for selected user (with date range to limit results)
  const loadTimeEntries = async (userId = null) => {
    setEntriesLoading(true);
    try {
      // Default to last 7 days for performance
      const endDate = new Date().toISOString();
      const startDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
      
      let params = `?start_date=${startDate}&end_date=${endDate}`;
      if (userId && userId !== "all") {
        params += `&user_id=${userId}`;
      }
      const res = await axios.get(`${API}/api/clockify/time-entries${params}`);
      setTimeEntries(res.data.entries || []);
    } catch (error) {
      console.error("Failed to load time entries:", error);
      toast.error("Failed to load time entries");
    }
    setEntriesLoading(false);
  };

  // Handle user filter change
  useEffect(() => {
    if (activeSubTab === "entries" && configStatus?.is_configured) {
      loadTimeEntries(selectedUser);
    }
  }, [selectedUser, activeSubTab, configStatus]);

  // Load after-hours data
  const loadAfterHoursData = useCallback(async () => {
    setAfterHoursLoading(true);
    try {
      // Build URL - weeks=0 means "this week" (current week only)
      const url = afterHoursWeeks === 0 
        ? `${API}/api/clockify/after-hours?this_week=true`
        : `${API}/api/clockify/after-hours?weeks=${afterHoursWeeks}`;
      
      // Use longer timeout since Clockify API can be slow
      const res = await axios.get(url, {
        timeout: 120000 // 2 minute timeout
      });
      setAfterHoursData(res.data);
      if (res.data?.summary?.total_entries === 0) {
        toast.info("No after-hours entries found for linked agents");
      }
    } catch (error) {
      console.error("Failed to load after-hours data:", error);
      if (error.code === 'ECONNABORTED') {
        toast.error("Request timed out - Clockify API is slow. Please try again.");
      } else {
        toast.error("Failed to load after-hours analysis");
      }
      // Set empty data so user can retry
      setAfterHoursData({ summary: { total_entries: 0 }, entries: [], user_summary: [] });
    }
    setAfterHoursLoading(false);
  }, [afterHoursWeeks]);

  // Load after-hours data when tab is selected
  useEffect(() => {
    if (activeSubTab === "afterhours" && configStatus?.is_configured && !afterHoursData) {
      loadAfterHoursData();
    }
  }, [activeSubTab, configStatus, afterHoursData, loadAfterHoursData]);

  // Load anomalies data
  const loadAnomaliesData = useCallback(async () => {
    setAnomaliesLoading(true);
    try {
      const res = await axios.get(`${API}/api/clockify/anomalies?days=14`);
      setAnomaliesData(res.data);
      toast.success("Anomaly detection complete");
    } catch (error) {
      console.error("Failed to detect anomalies:", error);
      toast.error("Failed to detect work pattern anomalies");
    }
    setAnomaliesLoading(false);
  }, []);

  // Format duration
  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  // If not configured, show setup screen
  if (!loading && (!configStatus || !configStatus.is_configured)) {
    return (
      <div className="space-y-6">
        <Card className="bg-[#F4F2ED] border-[#DAD5CA]">
          <CardHeader>
            <CardTitle className="text-[#1A1A1A] flex items-center gap-2">
              <Clock className="w-5 h-5 text-purple-500" />
              Clockify Timesheets
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center py-12">
            <Clock className="w-16 h-16 mx-auto mb-4 text-gray-500" />
            <h3 className="text-lg font-medium text-[#1A1A1A] mb-2">Clockify Not Configured</h3>
            <p className="text-gray-400 mb-6 max-w-md mx-auto">
              Connect your Clockify account to view team timesheets, time entries, and productivity analytics.
            </p>
            <Button
              onClick={() => setShowConfigDialog(true)}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="configure-clockify-btn"
            >
              <Settings className="w-4 h-4 mr-2" />
              Configure Clockify
            </Button>
          </CardContent>
        </Card>

        {/* Configuration Dialog */}
        <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
          <DialogContent className="bg-[#F4F2ED] border-[#DAD5CA] text-[#1A1A1A]">
            <DialogHeader>
              <DialogTitle>Configure Clockify Integration</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>API Key</Label>
                <Input
                  type="password"
                  placeholder="Enter your Clockify API key"
                  value={newApiKey}
                  onChange={(e) => setNewApiKey(e.target.value)}
                  className="bg-[#FFFFFF] border-[#DAD5CA]"
                  data-testid="clockify-api-key-input"
                />
                <p className="text-xs text-gray-400">
                  Get your API key from Clockify Settings → Profile → API
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowConfigDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={configureClockify}
                disabled={configuring}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="save-clockify-config-btn"
              >
                {configuring ? "Connecting..." : "Connect"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="timesheets-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#1A1A1A] flex items-center gap-2">
            <Clock className="w-6 h-6 text-purple-500" />
            Timesheets
          </h2>
          <p className="text-gray-400 text-sm mt-1">
            {configStatus?.workspace_name && `Workspace: ${configStatus.workspace_name}`}
          </p>
        </div>
        <Button
          onClick={loadData}
          disabled={loading}
          variant="outline"
          className="border-[#DAD5CA]"
          data-testid="refresh-timesheets-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-purple-500" />
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          {summary && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="bg-[#F4F2ED] border-[#DAD5CA]">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-400">Total Hours This Week</p>
                      <p className="text-2xl font-bold text-[#1A1A1A]">{summary.total_hours}h</p>
                    </div>
                    <Timer className="w-8 h-8 text-purple-500" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-[#F4F2ED] border-[#DAD5CA]">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-400">Active Users</p>
                      <p className="text-2xl font-bold text-[#1A1A1A]">{summary.active_users}</p>
                    </div>
                    <Users className="w-8 h-8 text-blue-500" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-[#F4F2ED] border-[#DAD5CA]">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-400">Time Entries</p>
                      <p className="text-2xl font-bold text-[#1A1A1A]">{summary.total_entries}</p>
                    </div>
                    <Calendar className="w-8 h-8 text-green-500" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-[#F4F2ED] border-[#DAD5CA]">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-400">Active Projects</p>
                      <p className="text-2xl font-bold text-[#1A1A1A]">{projects.length}</p>
                    </div>
                    <FolderOpen className="w-8 h-8 text-orange-500" />
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Sub-tabs */}
          <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
            <TabsList className="bg-[#FFFFFF] border border-[#DAD5CA]">
              <TabsTrigger value="summary" data-testid="timesheets-summary-tab">Team Summary</TabsTrigger>
              <TabsTrigger value="weekly" data-testid="timesheets-weekly-tab">Weekly Breakdown</TabsTrigger>
              <TabsTrigger value="afterhours" data-testid="timesheets-afterhours-tab">
                <Moon className="w-4 h-4 mr-1" />
                After Hours
              </TabsTrigger>
              <TabsTrigger value="entries" data-testid="timesheets-entries-tab">Time Entries</TabsTrigger>
              <TabsTrigger value="projects" data-testid="timesheets-projects-tab">Projects</TabsTrigger>
            </TabsList>

            {/* Team Summary Tab */}
            <TabsContent value="summary" className="space-y-4">
              <Card className="bg-[#F4F2ED] border-[#DAD5CA]">
                <CardHeader>
                  <CardTitle className="text-[#1A1A1A] text-lg">Team Hours This Week</CardTitle>
                </CardHeader>
                <CardContent>
                  {summary?.user_summary?.length > 0 ? (
                    <div className="space-y-4">
                      {summary.user_summary.map((user, idx) => (
                        <div key={user.user_id || idx} className="space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center text-[#1A1A1A] text-sm font-medium">
                                {user.user_name?.charAt(0) || "?"}
                              </div>
                              <div>
                                <p className="text-[#1A1A1A] font-medium">{user.user_name}</p>
                                <p className="text-xs text-gray-400">{user.user_email}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-[#1A1A1A] font-bold">{user.total_hours}h</p>
                              <p className="text-xs text-gray-400">{user.entry_count} entries</p>
                            </div>
                          </div>
                          <Progress 
                            value={Math.min((user.total_hours / 40) * 100, 100)} 
                            className="h-2 bg-[#DAD5CA]"
                          />
                          <div className="flex gap-2 flex-wrap">
                            {user.projects?.map((proj, pIdx) => (
                              <Badge key={pIdx} variant="outline" className="text-xs border-[#DAD5CA] text-gray-400">
                                {proj}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-400 text-center py-4">No time entries this week</p>
                  )}
                </CardContent>
              </Card>

              {/* Project Summary */}
              <Card className="bg-[#F4F2ED] border-[#DAD5CA]">
                <CardHeader>
                  <CardTitle className="text-[#1A1A1A] text-lg">Hours by Project</CardTitle>
                </CardHeader>
                <CardContent>
                  {summary?.project_summary?.length > 0 ? (
                    <div className="space-y-3">
                      {summary.project_summary.map((project, idx) => (
                        <div key={project.project_id || idx} className="flex items-center justify-between p-3 bg-[#FFFFFF] rounded-lg">
                          <div className="flex items-center gap-3">
                            <div 
                              className="w-3 h-3 rounded-full" 
                              style={{ backgroundColor: project.project_color || '#666' }}
                            />
                            <div>
                              <p className="text-[#1A1A1A] font-medium">{project.project_name}</p>
                              <p className="text-xs text-gray-400">
                                {project.users?.join(", ")}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-[#1A1A1A] font-bold">{project.total_hours}h</p>
                            <p className="text-xs text-gray-400">{project.entry_count} entries</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-400 text-center py-4">No project data</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Weekly Breakdown Tab */}
            <TabsContent value="weekly" className="space-y-4">
              <Card className="bg-[#F4F2ED] border-[#DAD5CA]">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-[#1A1A1A] text-lg">Weekly Breakdown</CardTitle>
                  <Select value={String(weeksBack)} onValueChange={(v) => setWeeksBack(Number(v))}>
                    <SelectTrigger className="w-[150px] bg-[#FFFFFF] border-[#DAD5CA]">
                      <SelectValue placeholder="Weeks" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#F4F2ED] border-[#DAD5CA]">
                      <SelectItem value="2">Last 2 weeks</SelectItem>
                      <SelectItem value="4">Last 4 weeks</SelectItem>
                      <SelectItem value="8">Last 8 weeks</SelectItem>
                      <SelectItem value="12">Last 12 weeks</SelectItem>
                    </SelectContent>
                  </Select>
                </CardHeader>
                <CardContent>
                  {weeklyData?.weekly_data?.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow className="border-[#DAD5CA]">
                          <TableHead className="text-gray-400">Week Starting</TableHead>
                          <TableHead className="text-gray-400">Total Hours</TableHead>
                          <TableHead className="text-gray-400">Entries</TableHead>
                          <TableHead className="text-gray-400">Top Contributors</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {weeklyData.weekly_data.map((week, idx) => (
                          <TableRow key={idx} className="border-[#DAD5CA]">
                            <TableCell className="text-[#1A1A1A] font-medium">
                              {new Date(week.week_start).toLocaleDateString('en-AU', {
                                day: 'numeric',
                                month: 'short'
                              })}
                            </TableCell>
                            <TableCell>
                              <span className="text-[#1A1A1A] font-bold">{week.total_hours}h</span>
                            </TableCell>
                            <TableCell className="text-gray-400">{week.entry_count}</TableCell>
                            <TableCell>
                              <div className="flex gap-2 flex-wrap">
                                {Object.entries(week.users || {})
                                  .sort(([,a], [,b]) => b - a)
                                  .slice(0, 3)
                                  .map(([name, hours], uIdx) => (
                                    <Badge 
                                      key={uIdx} 
                                      variant="outline" 
                                      className="text-xs border-[#DAD5CA] text-gray-300"
                                    >
                                      {name}: {hours}h
                                    </Badge>
                                  ))}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-gray-400 text-center py-4">No weekly data available</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* After Hours Tab */}
            <TabsContent value="afterhours" className="space-y-4">
              {afterHoursLoading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-6 h-6 animate-spin text-purple-500" />
                  <span className="ml-2 text-gray-400">Analyzing after-hours work...</span>
                </div>
              ) : afterHoursData ? (
                <>
                  {/* After Hours Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <Card className="bg-[#F4F2ED] border-[#DAD5CA]">
                      <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-400">Total After Hours</p>
                            <p className="text-2xl font-bold text-[#1A1A1A]">{afterHoursData.summary?.total_hours || 0}h</p>
                          </div>
                          <Moon className="w-8 h-8 text-indigo-500" />
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card className="bg-[#F4F2ED] border-[#DAD5CA]">
                      <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-400">Early Starts</p>
                            <p className="text-2xl font-bold text-amber-400">{afterHoursData.summary?.early_count || 0}</p>
                            <p className="text-xs text-gray-500">Before 7:30 AM</p>
                          </div>
                          <Sun className="w-8 h-8 text-amber-500" />
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card className="bg-[#F4F2ED] border-[#DAD5CA]">
                      <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-400">Late Finishes</p>
                            <p className="text-2xl font-bold text-orange-400">{afterHoursData.summary?.late_count || 0}</p>
                            <p className="text-xs text-gray-500">After 5:00 PM</p>
                          </div>
                          <Moon className="w-8 h-8 text-orange-500" />
                        </div>
                      </CardContent>
                    </Card>
                    
                    <Card className="bg-[#F4F2ED] border-[#DAD5CA]">
                      <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-400">Weekend Work</p>
                            <p className="text-2xl font-bold text-red-400">{afterHoursData.summary?.weekend_count || 0}</p>
                            <p className="text-xs text-gray-500">Sat & Sun entries</p>
                          </div>
                          <AlertTriangle className="w-8 h-8 text-red-500" />
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Work Hours Info */}
                  <div className="bg-indigo-900/20 border border-indigo-700/30 rounded-lg p-3">
                    <div className="flex items-center gap-2 text-indigo-400 text-sm">
                      <AlertCircle className="w-4 h-4" />
                      <span>
                        Normal work hours: {afterHoursData.default_work_hours?.start} - {afterHoursData.default_work_hours?.end} ({afterHoursData.default_work_hours?.timezone})
                      </span>
                    </div>
                  </div>

                  {/* After Hours by Agent */}
                  <Card className="bg-[#F4F2ED] border-[#DAD5CA]">
                    <CardHeader className="flex flex-row items-center justify-between">
                      <CardTitle className="text-[#1A1A1A] text-lg">After Hours by Agent</CardTitle>
                      <Select value={String(afterHoursWeeks)} onValueChange={(v) => { setAfterHoursWeeks(Number(v)); setAfterHoursData(null); }}>
                        <SelectTrigger className="w-[150px] bg-[#FFFFFF] border-[#DAD5CA]">
                          <SelectValue placeholder="Period" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#F4F2ED] border-[#DAD5CA]">
                          <SelectItem value="0">This week</SelectItem>
                          <SelectItem value="1">Last 7 days</SelectItem>
                          <SelectItem value="2">Last 2 weeks</SelectItem>
                          <SelectItem value="4">Last 4 weeks</SelectItem>
                        </SelectContent>
                      </Select>
                    </CardHeader>
                    <CardContent>
                      {afterHoursData.user_summary?.length > 0 ? (
                        <div className="space-y-4">
                          {afterHoursData.user_summary.map((user, idx) => (
                            <div key={user.user_id || idx} className="space-y-2 p-3 bg-[#FFFFFF] rounded-lg">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 rounded-full bg-indigo-600 flex items-center justify-center text-[#1A1A1A] font-medium">
                                    {user.user_name?.charAt(0) || "?"}
                                  </div>
                                  <div>
                                    <p className="text-[#1A1A1A] font-medium">{user.user_name}</p>
                                    <div className="flex gap-3 text-xs mt-1">
                                      <span className="text-amber-400">
                                        <Sun className="w-3 h-3 inline mr-1" />
                                        {user.early_count} early
                                      </span>
                                      <span className="text-orange-400">
                                        <Moon className="w-3 h-3 inline mr-1" />
                                        {user.late_count} late
                                      </span>
                                      {user.weekend_count > 0 && (
                                        <span className="text-red-400">
                                          <AlertTriangle className="w-3 h-3 inline mr-1" />
                                          {user.weekend_count} weekend
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p className="text-[#1A1A1A] font-bold text-lg">{user.total_after_hours}h</p>
                                  <p className="text-xs text-gray-400">{user.total_entries} entries</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-gray-400 text-center py-4">No after-hours work recorded</p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Weekly After Hours Chart */}
                  <Card className="bg-[#F4F2ED] border-[#DAD5CA]">
                    <CardHeader>
                      <CardTitle className="text-[#1A1A1A] text-lg">Weekly After Hours Breakdown</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {afterHoursData.weekly_breakdown?.length > 0 ? (
                        <Table>
                          <TableHeader>
                            <TableRow className="border-[#DAD5CA]">
                              <TableHead className="text-gray-400">Week Starting</TableHead>
                              <TableHead className="text-gray-400">Early</TableHead>
                              <TableHead className="text-gray-400">Late</TableHead>
                              <TableHead className="text-gray-400">Weekend</TableHead>
                              <TableHead className="text-gray-400">Total Hours</TableHead>
                              <TableHead className="text-gray-400">Contributors</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {afterHoursData.weekly_breakdown.map((week, idx) => (
                              <TableRow key={idx} className="border-[#DAD5CA]">
                                <TableCell className="text-[#1A1A1A] font-medium">
                                  {new Date(week.week_start).toLocaleDateString('en-AU', {
                                    day: 'numeric',
                                    month: 'short'
                                  })}
                                </TableCell>
                                <TableCell>
                                  <Badge className="bg-amber-600/20 text-amber-400 border-0">
                                    {week.early_count}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  <Badge className="bg-orange-600/20 text-orange-400 border-0">
                                    {week.late_count}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  <Badge className="bg-red-600/20 text-red-400 border-0">
                                    {week.weekend_count}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-[#1A1A1A] font-bold">{week.total_hours}h</TableCell>
                                <TableCell>
                                  <div className="flex gap-2 flex-wrap">
                                    {Object.entries(week.users || {})
                                      .sort(([,a], [,b]) => b - a)
                                      .map(([name, hours], uIdx) => (
                                        <Badge 
                                          key={uIdx} 
                                          variant="outline" 
                                          className="text-xs border-[#DAD5CA] text-gray-300"
                                        >
                                          {name}: {hours}h
                                        </Badge>
                                      ))}
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <p className="text-gray-400 text-center py-4">No weekly data available</p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Recent After Hours Entries */}
                  <Card className="bg-[#F4F2ED] border-[#DAD5CA]">
                    <CardHeader>
                      <CardTitle className="text-[#1A1A1A] text-lg">Recent After Hours Entries</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {afterHoursData.entries?.length > 0 ? (
                        <Table>
                          <TableHeader>
                            <TableRow className="border-[#DAD5CA]">
                              <TableHead className="text-gray-400">Date</TableHead>
                              <TableHead className="text-gray-400">Agent</TableHead>
                              <TableHead className="text-gray-400">Local Time</TableHead>
                              <TableHead className="text-gray-400">Type</TableHead>
                              <TableHead className="text-gray-400">Project</TableHead>
                              <TableHead className="text-gray-400">Duration</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {afterHoursData.entries.slice(0, 20).map((entry, idx) => (
                              <TableRow key={entry.id || idx} className="border-[#DAD5CA]">
                                <TableCell className="text-gray-400">
                                  {entry.start && new Date(entry.start).toLocaleDateString('en-AU', {
                                    day: 'numeric',
                                    month: 'short'
                                  })}
                                </TableCell>
                                <TableCell className="text-[#1A1A1A]">{entry.user_name}</TableCell>
                                <TableCell className="text-[#1A1A1A] font-mono text-sm">
                                  {entry.local_time?.split(' ')[1] || '-'}
                                </TableCell>
                                <TableCell>
                                  {entry.after_hours_reason === 'early' && (
                                    <Badge className="bg-amber-600/20 text-amber-400 border-0">
                                      <Sun className="w-3 h-3 mr-1" />
                                      {entry.minutes_early}min early
                                    </Badge>
                                  )}
                                  {entry.after_hours_reason === 'late' && (
                                    <Badge className="bg-orange-600/20 text-orange-400 border-0">
                                      <Moon className="w-3 h-3 mr-1" />
                                      {entry.minutes_late}min late
                                    </Badge>
                                  )}
                                  {entry.after_hours_reason === 'weekend' && (
                                    <Badge className="bg-red-600/20 text-red-400 border-0">
                                      <AlertTriangle className="w-3 h-3 mr-1" />
                                      {entry.day_name}
                                    </Badge>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    {entry.project_color && (
                                      <div 
                                        className="w-2 h-2 rounded-full" 
                                        style={{ backgroundColor: entry.project_color }}
                                      />
                                    )}
                                    <span className="text-gray-300">{entry.project_name || "—"}</span>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <Badge className="bg-indigo-600/20 text-indigo-400 border-0">
                                    {entry.duration_formatted}
                                  </Badge>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <p className="text-gray-400 text-center py-4">No after-hours entries found</p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Work Pattern Anomalies Section */}
                  <Card className="bg-[#F4F2ED] border-[#DAD5CA]">
                    <CardHeader className="flex flex-row items-center justify-between">
                      <div>
                        <CardTitle className="text-[#1A1A1A] text-lg flex items-center gap-2">
                          <AlertTriangle className="w-5 h-5 text-amber-500" />
                          Work Pattern Anomalies
                        </CardTitle>
                        <p className="text-xs text-gray-400 mt-1">
                          Automatic detection of unusual work patterns
                        </p>
                      </div>
                      <Button
                        onClick={loadAnomaliesData}
                        disabled={anomaliesLoading}
                        variant="outline"
                        size="sm"
                        className="border-amber-600/50 text-amber-400 hover:bg-amber-900/20"
                        data-testid="detect-anomalies-btn"
                      >
                        {anomaliesLoading ? (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Analyzing...
                          </>
                        ) : (
                          <>
                            <AlertTriangle className="w-4 h-4 mr-2" />
                            Detect Anomalies
                          </>
                        )}
                      </Button>
                    </CardHeader>
                    <CardContent>
                      {anomaliesLoading ? (
                        <div className="flex items-center justify-center py-8">
                          <RefreshCw className="w-6 h-6 animate-spin text-amber-500" />
                          <span className="ml-2 text-gray-400">Analyzing work patterns (this may take a moment)...</span>
                        </div>
                      ) : anomaliesData ? (
                        <div className="space-y-4">
                          {/* Summary */}
                          <div className="grid grid-cols-4 gap-3">
                            <div className="p-3 bg-red-900/20 rounded-lg border border-red-800/30">
                              <p className="text-xs text-red-400">Critical</p>
                              <p className="text-2xl font-bold text-red-400">{anomaliesData.summary?.critical || 0}</p>
                            </div>
                            <div className="p-3 bg-orange-900/20 rounded-lg border border-orange-800/30">
                              <p className="text-xs text-orange-400">High</p>
                              <p className="text-2xl font-bold text-orange-400">{anomaliesData.summary?.high || 0}</p>
                            </div>
                            <div className="p-3 bg-amber-900/20 rounded-lg border border-amber-800/30">
                              <p className="text-xs text-amber-400">Warning</p>
                              <p className="text-2xl font-bold text-amber-400">{anomaliesData.summary?.warning || 0}</p>
                            </div>
                            <div className="p-3 bg-[#FFFFFF] rounded-lg border border-[#DAD5CA]">
                              <p className="text-xs text-gray-400">Agents Analyzed</p>
                              <p className="text-2xl font-bold text-gray-300">{anomaliesData.summary?.agents_analyzed || 0}</p>
                            </div>
                          </div>

                          {/* Anomaly List */}
                          {anomaliesData.anomalies?.length > 0 ? (
                            <div className="space-y-3">
                              {anomaliesData.anomalies.map((anomaly, idx) => (
                                <div 
                                  key={idx}
                                  className={`p-4 rounded-lg border ${
                                    anomaly.severity === 'critical' ? 'bg-red-900/10 border-red-800/50' :
                                    anomaly.severity === 'high' ? 'bg-orange-900/10 border-orange-800/50' :
                                    'bg-amber-900/10 border-amber-800/50'
                                  }`}
                                >
                                  <div className="flex items-start justify-between">
                                    <div className="flex items-start gap-3">
                                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                        anomaly.severity === 'critical' ? 'bg-red-600' :
                                        anomaly.severity === 'high' ? 'bg-orange-600' :
                                        'bg-amber-600'
                                      }`}>
                                        {anomaly.type === 'consistent_early_start' && <Sun className="w-5 h-5 text-[#1A1A1A]" />}
                                        {anomaly.type === 'consistent_late_finish' && <Moon className="w-5 h-5 text-[#1A1A1A]" />}
                                        {anomaly.type === 'day_off_work' && <Calendar className="w-5 h-5 text-[#1A1A1A]" />}
                                        {anomaly.type === 'potential_burnout' && <AlertTriangle className="w-5 h-5 text-[#1A1A1A]" />}
                                      </div>
                                      <div>
                                        <p className="text-[#1A1A1A] font-medium">{anomaly.user_name}</p>
                                        <p className={`text-sm font-medium ${
                                          anomaly.severity === 'critical' ? 'text-red-400' :
                                          anomaly.severity === 'high' ? 'text-orange-400' :
                                          'text-amber-400'
                                        }`}>{anomaly.title}</p>
                                        <p className="text-sm text-gray-400 mt-1">{anomaly.description}</p>
                                      </div>
                                    </div>
                                    <Badge className={`${
                                      anomaly.severity === 'critical' ? 'bg-red-600/20 text-red-400' :
                                      anomaly.severity === 'high' ? 'bg-orange-600/20 text-orange-400' :
                                      'bg-amber-600/20 text-amber-400'
                                    } border-0`}>
                                      {anomaly.severity.toUpperCase()}
                                    </Badge>
                                  </div>
                                  <div className="mt-3 pt-3 border-t border-[#DAD5CA]">
                                    <p className="text-xs text-gray-400">
                                      <span className="text-gray-500">Recommendation:</span> {anomaly.recommendation}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-6">
                              <Check className="w-12 h-12 mx-auto mb-3 text-green-500" />
                              <p className="text-green-400 font-medium">No anomalies detected</p>
                              <p className="text-gray-400 text-sm">All agents are working within their scheduled hours</p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-center py-6 text-gray-400">
                          <AlertTriangle className="w-10 h-10 mx-auto mb-3 text-gray-500" />
                          <p>Click "Detect Anomalies" to analyze work patterns</p>
                          <p className="text-xs mt-1 text-gray-500">Analyzes the last 14 days of time entries</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </>
              ) : (
                <Card className="bg-[#F4F2ED] border-[#DAD5CA]">
                  <CardContent className="text-center py-12">
                    <Moon className="w-16 h-16 mx-auto mb-4 text-gray-500" />
                    <h3 className="text-lg font-medium text-[#1A1A1A] mb-2">No After Hours Data</h3>
                    <p className="text-gray-400 mb-4">Click below to analyze after-hours work patterns</p>
                    <Button
                      onClick={loadAfterHoursData}
                      className="bg-indigo-600 hover:bg-indigo-700"
                    >
                      <Moon className="w-4 h-4 mr-2" />
                      Analyze After Hours
                    </Button>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Time Entries Tab */}
            <TabsContent value="entries" className="space-y-4">
              <Card className="bg-[#F4F2ED] border-[#DAD5CA]">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-[#1A1A1A] text-lg">Recent Time Entries</CardTitle>
                    <p className="text-xs text-gray-400 mt-1">Showing last 7 days</p>
                  </div>
                  <Select value={selectedUser} onValueChange={setSelectedUser}>
                    <SelectTrigger className="w-[200px] bg-[#FFFFFF] border-[#DAD5CA]">
                      <SelectValue placeholder="Filter by user" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#F4F2ED] border-[#DAD5CA]">
                      <SelectItem value="all">All Users</SelectItem>
                      {users.map(user => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </CardHeader>
                <CardContent>
                  {entriesLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <RefreshCw className="w-6 h-6 animate-spin text-purple-500" />
                      <span className="ml-2 text-gray-400">Loading time entries...</span>
                    </div>
                  ) : timeEntries.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow className="border-[#DAD5CA]">
                          <TableHead className="text-gray-400">Date</TableHead>
                          <TableHead className="text-gray-400">User</TableHead>
                          <TableHead className="text-gray-400">Description</TableHead>
                          <TableHead className="text-gray-400">Project</TableHead>
                          <TableHead className="text-gray-400">Duration</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {timeEntries.slice(0, 100).map((entry, idx) => (
                          <TableRow key={entry.id || idx} className="border-[#DAD5CA]">
                            <TableCell className="text-gray-400">
                              {entry.start && new Date(entry.start).toLocaleDateString('en-AU', {
                                day: 'numeric',
                                month: 'short'
                              })}
                            </TableCell>
                            <TableCell className="text-[#1A1A1A]">{entry.user_name}</TableCell>
                            <TableCell className="text-[#1A1A1A] max-w-[300px] truncate">
                              {entry.description || <span className="text-gray-500 italic">No description</span>}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {entry.project_color && (
                                  <div 
                                    className="w-2 h-2 rounded-full" 
                                    style={{ backgroundColor: entry.project_color }}
                                  />
                                )}
                                <span className="text-gray-300">{entry.project_name || "—"}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge className="bg-purple-600/20 text-purple-400 border-0">
                                {entry.duration_formatted}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-gray-400 text-center py-8">
                      No time entries found for this period
                    </p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Projects Tab */}
            <TabsContent value="projects" className="space-y-4">
              <Card className="bg-[#F4F2ED] border-[#DAD5CA]">
                <CardHeader>
                  <CardTitle className="text-[#1A1A1A] text-lg">Active Projects</CardTitle>
                </CardHeader>
                <CardContent>
                  {projects.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {projects.map(project => (
                        <div 
                          key={project.id}
                          className="p-4 bg-[#FFFFFF] rounded-lg border border-[#DAD5CA]"
                        >
                          <div className="flex items-center gap-3 mb-2">
                            <div 
                              className="w-4 h-4 rounded" 
                              style={{ backgroundColor: project.color || '#666' }}
                            />
                            <h4 className="text-[#1A1A1A] font-medium">{project.name}</h4>
                          </div>
                          {project.client_name && (
                            <p className="text-sm text-gray-400 mb-2">
                              Client: {project.client_name}
                            </p>
                          )}
                          <div className="flex gap-2">
                            {project.billable && (
                              <Badge className="bg-green-600/20 text-green-400 border-0 text-xs">
                                Billable
                              </Badge>
                            )}
                            {project.archived && (
                              <Badge className="bg-gray-600/20 text-gray-400 border-0 text-xs">
                                Archived
                              </Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-400 text-center py-8">No projects found</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}
